import { defineStore } from 'pinia';
import {
  cutoutKey,
  useFilterStore,
} from '@/stores/Configurator/FilterStore.ts';
import { computed, type ComputedRef, ref, type Ref } from 'vue';
import _ from 'lodash';
import { FilterValueRecord } from '@/types/Filters/FilterValueRecord.ts';
import type { SelectorOption } from '@/types/Configurator/SelectorOption';
import {
  type PriceTableMobileAllPricesObject,
  type PriceTableMobileFavoritePricesObject,
} from '@/types/joostconnector';
import { useSizeStore } from '@/stores/Configurator/SizeStore.ts';
import { useProductPage } from '@/composables/ProductPage.ts';
import I18n from '@/i18n.ts';
import { ImageCutoutEnum } from '@/types/Enums/ImageCutoutEnum.ts';

export const usePriceTableStore = defineStore(
  `price-table-${window.store_code}`,
  () => {
    const sizeStore = useSizeStore();
    const filterStore = useFilterStore();

    const hasFilters: ComputedRef<boolean> = computed(
      () => Object.keys(filterStore.filters)?.length > 0
    );

    const popupOpen: Ref<boolean> = ref(false);
    const showMore: Ref<boolean> = ref(false);
    const firstFilterKey = computed(() => _.keys(filterStore.filters)[0]);
    const firstFilterValue = computed(() => _.values(filterStore.filters)[0]);

    const isFavoriteOptionSelected: ComputedRef<boolean> = computed(
      () =>
        selectedFilterOptions.value[firstFilterKey.value]?.value === 'favorite'
    );

    const selectedFiltersString: ComputedRef<string> = computed(() =>
      _.join(_.map(originalSelectedFilterOptions.value, 'label'), ', ')
    );

    const selectedFilterValues: ComputedRef<FilterValueRecord> = computed(() =>
      _.mapValues(
        filterStore.filters,
        (filter, key) =>
          filter.values[originalSelectedFilterOptions.value[key].value]
      )
    );

    const otherFilters = computed(() =>
      _.fromPairs(_.toPairs(filterStore.filters).slice(1))
    );

    const filterOptions: ComputedRef<Record<string, SelectorOption[]>> =
      computed(() =>
        _.mapValues(filterStore.filters, filter =>
          _.map(filter.values, value =>
            filterStore.filterValueToDropdown(value, filter)
          )
        )
      );

    const priceRows: ComputedRef<
      Record<string, PriceTableMobileAllPricesObject>
    > = computed(() => {
      if (isFavoriteOptionSelected.value) {
        return _.keyBy(favoritePriceRows.value, 'sku');
      }

      return _(allPriceRows.value)
        .filter(sizeValue =>
          _.every(
            filterStore.filters,
            (_, key: string) =>
              sizeValue[key as keyof typeof sizeValue] ===
              selectedFilterOptions.value[key]?.label
          )
        )
        .sortBy(['width', 'height'])
        .keyBy('sku')
        .value();
    });

    const limitedPriceRows: ComputedRef<PriceTableMobileAllPricesObject[]> =
      computed(() => _.take(Object.values(priceRows.value), 12));

    const mostChosenSku: ComputedRef<string | null> = computed(() => {
      // Get the filter which has default sku (at this time it's possible to only add 1 default product filter to a configurable product)
      const selectedFilter = _.find(
        selectedFilterValues.value,
        filter => !_.isNil(filter?.defaultSku)
      );

      // Get default size for filter
      const defaultSizeForFilter = _.find(
        allPriceRows.value,
        (priceRow: PriceTableMobileAllPricesObject) =>
          priceRow.sku === selectedFilter?.defaultSku
      ) as PriceTableMobileAllPricesObject;

      // Get default sku which has the same width and height as the default filter sku
      const defaultSkuForSize = _.find(
        priceRows.value,
        (priceRow: PriceTableMobileAllPricesObject) =>
          priceRow.width === defaultSizeForFilter?.width &&
          priceRow.height === defaultSizeForFilter?.height
      ) as PriceTableMobileAllPricesObject;

      // Return the right sku
      return (
        defaultSkuForSize?.sku ??
        selectedFilter?.defaultSku ??
        sizeStore.getDefaultSku()
      );
    });

    const favoritePriceRows: ComputedRef<
      PriceTableMobileFavoritePricesObject[]
    > = computed(() => window.price_table.favoritePrices ?? []);

    const allPriceRows: ComputedRef<PriceTableMobileAllPricesObject[]> =
      computed(() => window.price_table.allPrices ?? []);

    const hasAnyRecommendedPrice: ComputedRef<boolean> = computed(() => {
      const rows = _.filter(
        priceRows.value,
        row => (row.recommendedPrice ?? 0) > row.price
      );

      return rows.length > 0;
    });

    const firstFilterOptions = computed(() => {
      let options = filterStore.filterOptions[firstFilterKey.value];

      if (!options) {
        return [];
      }

      if (!_.isEmpty(favoritePriceRows.value)) {
        const firstOptionClone = _.cloneDeep(options[0]);
        firstOptionClone.label = I18n.global.t(
          'general.price_table.filters.favorite_sizes'
        );
        firstOptionClone.value = 'favorite';
        options = _.concat([firstOptionClone], options);
      }

      return options;
    });

    // Used for the dropdown filters
    const selectedFilterOptions: Ref<Record<string, SelectorOption>> = ref(
      _.mapValues(filterStore.filters, (filter, index) =>
        index === firstFilterKey.value
          ? (_.first(firstFilterOptions.value) as SelectorOption)
          : filterStore.filterValueToDropdown(
              filterStore.getDefaultFilter(filter)
            )
      )
    );

    // Used for the actual price table
    const originalSelectedFilterOptions: Ref<Record<string, SelectorOption>> =
      ref(
        _.mapValues(filterStore.filters, (filter, index) =>
          index === firstFilterKey.value
            ? (_.first(firstFilterOptions.value) as SelectorOption)
            : filterStore.filterValueToDropdown(
                filterStore.getDefaultFilter(filter)
              )
        )
      );

    // Used to reset the selected filter options when the popup is closed (not saved)
    const resetSelectedFilterOptionsList: Ref<Record<string, SelectorOption>> =
      ref(
        _.mapValues(filterStore.filters, (filter, index) =>
          index === firstFilterKey.value
            ? (_.first(firstFilterOptions.value) as SelectorOption)
            : filterStore.filterValueToDropdown(
                filterStore.getDefaultFilter(filter)
              )
        )
      );

    function togglePriceFilterPopup(save: boolean = true): void {
      popupOpen.value = !popupOpen.value;

      if (popupOpen.value) {
        resetSelectedFilterOptionsList.value = _.cloneDeep(
          originalSelectedFilterOptions.value
        );

        useProductPage()
          .hideProductPage(getPage(), true)
          .then(() => {});
        return;
      }
      useProductPage().showProductPage(getPage());

      if (!save) {
        originalSelectedFilterOptions.value =
          resetSelectedFilterOptionsList.value;
        selectedFilterOptions.value = resetSelectedFilterOptionsList.value;
        return;
      }

      originalSelectedFilterOptions.value = selectedFilterOptions.value;
    }

    function getPage(): HTMLBodyElement {
      const pageWrapper = document.querySelector(
        '#html-body'
      ) as HTMLBodyElement;

      if (!pageWrapper) {
        throw new Error("Can't find page wrapper, something is very wrong");
      }

      return pageWrapper;
    }

    function setDefaultCutoutFilterValue() {
      if (isFavoriteOptionSelected.value) {
        return;
      }

      const cutoutFilterOptions = filterStore.filterOptions[cutoutKey];

      if (!cutoutFilterOptions) {
        return;
      }

      const defaultSku = sizeStore.selectedSizeValue.attributes;
      let filterCutoutValue: ImageCutoutEnum | null = ImageCutoutEnum.Square;

      // Landscape
      if (defaultSku?.width > defaultSku?.height) {
        filterCutoutValue = ImageCutoutEnum.Landscape;
      }

      // Portrait
      if (defaultSku?.width < defaultSku?.height) {
        filterCutoutValue = ImageCutoutEnum.Portrait;
      }

      const selectedOption = _.find(
        cutoutFilterOptions,
        filter => Number(filter.value) === Number(filterCutoutValue?.valueOf())
      );

      if (selectedOption) {
        originalSelectedFilterOptions.value[cutoutKey] = selectedOption;
        selectedFilterOptions.value[cutoutKey] = selectedOption;
      }
    }

    return {
      hasFilters,
      popupOpen,
      showMore,
      firstFilterKey,
      firstFilterValue,
      firstFilterOptions,
      selectedFilterValues,
      priceRows,
      selectedFiltersString,
      otherFilters,
      filterOptions,
      mostChosenSku,
      limitedPriceRows,
      selectedFilterOptions,
      originalSelectedFilterOptions,
      isFavoriteOptionSelected,
      hasAnyRecommendedPrice,
      togglePriceFilterPopup,
      setDefaultCutoutFilterValue,
    };
  }
);
